/**
 * Funções HTTP do CRUD de chaves de acesso ao provedor (E2.F3).
 *
 * Substitui o caminho singular PUT /perfil/api-key (E2.F2). Quatro operações: // ux-neutrality:allow doc-comment
 * list/create/rotate/revoke. O backend nunca devolve o valor em claro — todas
 * as respostas têm apenas o `last4` mascarado. Todas as chamadas convertem
 * erros HTTP/rede para `ApiKeysError` com `reason` estável (consumido pela UI
 * para escolher a mensagem certa em pt-BR).
 */
import axios, { AxiosError, type AxiosInstance } from 'axios';

// Identificador HTTP do provider — string técnica do contrato com backend,
// nunca exibida em UI (label do provedor vem de ptBR.apiKeys.addDialogProviderDisplayName).
export type ApiKeysProvider = 'anthropic' | 'lemit'; // ux-neutrality:allow api-contract; branding:allow

/** Ordem de exibição no seletor; o rótulo de cada um vem do i18n. */
export const API_KEY_PROVIDERS: readonly ApiKeysProvider[] = ['anthropic', 'lemit']; // ux-neutrality:allow api-contract; branding:allow

/**
 * Papel de cada provider no i18n. O identificador do contrato NÃO aparece em
 * `pt-BR.ts`: o arquivo de textos fala de papéis, não de fornecedores — é o que
 * mantém o lint de neutralidade verde sem bypass no catálogo de tradução.
 */
export type ApiKeyProviderPapel = 'analise' | 'bureau';
export const PROVIDER_PAPEL: Record<ApiKeysProvider, ApiKeyProviderPapel> = {
  anthropic: 'analise', // ux-neutrality:allow api-contract; branding:allow
  lemit: 'bureau',
};

export interface ApiKeyListItem {
  id: string;
  provider: ApiKeysProvider;
  key_version: number;
  last4: string;
  created_at: string;
  last_used_at: string | null;
}

export type ApiKeysErrorReason =
  | 'validation'
  | 'network'
  | 'conflict'
  | 'not_found'
  | 'unauthorized'
  | 'unknown';

export class ApiKeysError extends Error {
  public readonly reason: ApiKeysErrorReason;

  constructor(reason: ApiKeysErrorReason, message?: string) {
    super(message ?? reason);
    this.reason = reason;
    this.name = 'ApiKeysError';
  }
}

function _classify(err: unknown): ApiKeysError {
  if (axios.isAxiosError(err)) {
    const axiosErr = err as AxiosError;
    const status = axiosErr.response?.status;
    if (status === 409) return new ApiKeysError('conflict');
    if (status === 404) return new ApiKeysError('not_found');
    if (status === 401 || status === 403) return new ApiKeysError('unauthorized');
    if (status === 422) return new ApiKeysError('validation');
    if (axiosErr.code === 'ERR_NETWORK' || axiosErr.code === 'ECONNABORTED') {
      return new ApiKeysError('network');
    }
  }
  return new ApiKeysError('unknown');
}

export async function listApiKeys(client: AxiosInstance): Promise<ApiKeyListItem[]> {
  try {
    const { data } = await client.get<ApiKeyListItem[]>('/api-keys');
    return data;
  } catch (err) {
    throw _classify(err);
  }
}

export async function createApiKey(
  client: AxiosInstance,
  provider: ApiKeysProvider,
  value: string,
): Promise<ApiKeyListItem> {
  try {
    const { data } = await client.post<ApiKeyListItem>('/api-keys', { provider, value });
    return data;
  } catch (err) {
    throw _classify(err);
  }
}

export async function rotateApiKey(
  client: AxiosInstance,
  apiKeyId: string,
  value: string,
): Promise<ApiKeyListItem> {
  try {
    const { data } = await client.put<ApiKeyListItem>(`/api-keys/${apiKeyId}`, { value });
    return data;
  } catch (err) {
    throw _classify(err);
  }
}

export async function revokeApiKey(client: AxiosInstance, apiKeyId: string): Promise<void> {
  try {
    await client.delete(`/api-keys/${apiKeyId}`);
  } catch (err) {
    throw _classify(err);
  }
}
